import {Platform} from 'react-native';
import {origin} from 'config/app';
import {LIVE, LIVE_CF, SANDBOX, SANDBOX_CF} from 'config/hosts';
import {getCookie} from 'utils/cookies';
import {getEnvOverride} from 'utils/env';

let _customSubdomain: string;

// ENVIRONMENTS

export function isLive() {
  if (!isWeb()) return getEnvOverride() !== 'sandbox';
  return window.location.origin === LIVE
    || window.location.origin === LIVE_CF;
}

export function isSandbox() {
  if (!isWeb()) return getEnvOverride() === 'sandbox';
  return (window.location.origin === SANDBOX
    || window.location.origin === SANDBOX_CF);
}

export function isPreview() {
  if (!isWeb()) return false;
  return window.location.origin.includes('pages.dev');
}

export function isLocal() {
  if (!isWeb()) return false;
  if (__DEV__) return true;
  return window.location.origin === 'http://localhost:8080'
    || window.location.origin === `http://local.${origin}`
    || window.location.origin.indexOf('http://192.168') !== -1
    || window.location.origin === 'http://local-app.mediafire.com';
}

// IDENTIFIERS

export function getEnvironment() {
  if (isLive() || isLocal())
    return 'www';
  if (isSandbox() || isPreview())
    return 'sandbox';
  return 'www';
}

export function getHost(noSSL?: boolean) {
  const protocol = noSSL ? 'http' : 'https';
  if (_customSubdomain)
    return `${protocol}://${_customSubdomain}.${origin}`;
  if (isLive() || isLocal())
    return `${protocol}://www.${origin}`;
  if (isSandbox() || isPreview())
    return `${protocol}://sandbox.${origin}`;
  return `${protocol}://www.${origin}`;
}

export function getClient() {
  if (isWeb()) {
    return navigator.userAgent.slice(0, 255);
  } else if (isAndroid()) {
    return 'android';
  } else if (isWindows()) {
    return 'windows';
  } else if (isMacOS()) {
    return 'macos';
  } else if (isIOS()) {
    return 'ios';
  }
  return 'unknown';
}

export function getCustomSubdomain() {
  return _customSubdomain;
}

export function setCustomSubdomain(subdomain: string) {
  _customSubdomain = subdomain;
}

// PLATFORMS

export function isTouch() {
  switch (Platform.OS) {
    case 'android': return true;
    case 'ios': return true;
    case 'macos': return false;
    case 'windows': return false;
    case 'web': return ('ontouchstart' in window
      || (navigator && navigator.maxTouchPoints > 0))
      && (isLocal() || (
        navigator.platform !== 'Win32'
        && navigator.platform !== 'Win64'
        && navigator.platform !== 'MacIntel')
    );
    default: return false;
  }
}

export function isWeb() {
  return Platform.OS === 'web';
}

export function isNative() {
  return isAndroid() || isIOS() || isMacOS() || isWindows();
}

export function isAndroid() {
  return Platform.OS === 'android';
}

export function isIOS() {
  return Platform.OS === 'ios';
}

export function isMacOS() {
  return Platform.OS === 'macos';
}

export function isWindows() {
  return Platform.OS === 'windows';
}

export function isSafari() {
  return isWeb()
    && navigator.userAgent.indexOf('Safari/') !== -1
    && navigator.userAgent.indexOf('Chrome/') === -1;
}

export function isIOSSafari() {
  if (!isSafari()) return false;
  const ua = navigator.userAgent;
  if (/iP(hone|ad|od)/.test(ua)) return true;
  // iPadOS 13+ reports a Mac-like UA by default — detect via touch
  return /Macintosh/.test(ua) && 'ontouchend' in document;
}

export function isNativeAppCookieSet() {
  const value = getCookie('mf_native_app');
  return value === 'android' || value === 'ios';
}

export function isUpsellDeniedPlatform() {
  return isIOS() || isAndroid() || isIOSSafari() || isNativeAppCookieSet();
}

export function isIE11() {
  return isWeb()
    && !!window['MSInputMethodContext']
    && !!document['documentMode'];
}
